import {
  objectToQueryString,
  setQueryStringParam,
  getQueryStringParams,
  deleteQueryStringParam,
  parseIdsFromQueryParam,
  sanitizeIds,
} from "../../shared/utils";
import tippy from "tippy.js";

export function triageResultsModal({
  triageResultsUrl,
  teamBreakdownUrl,
  snapshotBaseUrl,
  triageImprovementsUrl,
  filteredResultsUrl,
  exportUrl,
  triageLabels,
}) {
  const sectionFromParams = getQueryStringParams().get("triage_section");
  const validSections = ["overview", "escalation", "improvement_area", "noop"];
  const initialSection = validSections.includes(sectionFromParams)
    ? sectionFromParams
    : "overview";

  return {
    open: false,
    initialLoad: false,
    showImprovementsTab: false,
    squadIds: [],
    tagIds: [],
    branchId: null,
    tab: null,
    triageLabels,
    selectedDriver: null,
    selectedSection: initialSection,
    responses: {
      loaded: false,
      data: [],
      numSquads: 0,
      squadsCompleted: 0,
      eligibleSquads: 0,
      expandedDriver: {},
      breakdownLoaded: false,
      breakdownData: {},
      expandedOpt: "escalation",
      sort: "votes",
      sortDir: "asc",
      lastUpdatedFilter: null,
      triageSortMenuOpen: false,
    },
    improvements: {
      loaded: false,
      data: {},
      sort: "squad",
      sortDir: "asc",
      driverFilter: "",
    },
    escalation: {
      loaded: false,
      data: [],
      sortBy: "score",
      sortReverse: false,
    },
    improvement_area: {
      loaded: false,
      data: [],
      sortBy: "score",
      sortReverse: false,
    },
    noop: {
      loaded: false,
      data: [],
      sortBy: "score",
      sortReverse: false,
    },
    filteredResultsLoading: false,
    comments: {
      open: false,
      loaded: false,
      driver: null,
      triageSelection: "",
      data: [],
    },
    init() {
      const queryParams = getQueryStringParams();
      const tabFromParams = queryParams.get("triage_tab"); // This is also used in app/controllers/kaizen/latest_snapshot_controller.rb
      const updateFromParams = queryParams.get("triage_updated");
      const branchFromParams = queryParams.get("triage_branch");
      const squadIdsFromParams = parseIdsFromQueryParam("squad_ids");
      const tagIdsFromParams = parseIdsFromQueryParam("tag_ids");

      if (squadIdsFromParams?.length) {
        this.squadIds = squadIdsFromParams;
      }
      if (tagIdsFromParams?.length) this.tagIds = tagIdsFromParams;
      if (branchFromParams?.length) this.branchId = branchFromParams;
      if (updateFromParams?.length)
        this.responses.lastUpdatedFilter = updateFromParams;

      if (tabFromParams == "responses") {
        this.openResultsModal();
      }

      if (tabFromParams == "improvements") {
        this.openResultsModal();
        this.setTab("improvements");
      }

      // Trigger initial fetch if on a filtered section
      if (
        ["escalation", "improvement_area", "noop"].includes(
          this.selectedSection,
        )
      ) {
        this.fetchFilteredResults(this.selectedSection);
      }
    },
    openResultsModal() {
      this.open = true;
      this.setTab("responses");
      this.updateQueryParams();
    },
    updateQueryParams() {
      setQueryStringParam("triage_tab", this.tab);
      setQueryStringParam("squad_ids", this.squadIds?.join(",") || null);
      setQueryStringParam("tag_ids", this.tagIds?.join(",") || null);
      setQueryStringParam("triage_updated", this.responses.lastUpdatedFilter);
      setQueryStringParam("triage_branch", this.branchId);
    },
    clearTriageQueryParams() {
      deleteQueryStringParam("triage_tab");
      deleteQueryStringParam("squad_ids");
      deleteQueryStringParam("tag_ids");
      deleteQueryStringParam("triage_updated");
      deleteQueryStringParam("triage_branch");
      deleteQueryStringParam("triage_section");

      // Clear selected driver when modal closes
      this.selectedDriver = null;
    },
    setTab(tab) {
      if (this.tab === tab) return;

      this.tab = tab;
      this.updateQueryParams();

      if (this.tab == "improvements" && !this.improvements.loaded) {
        this.fetchImprovementsData();
      }

      if (this.tab == "responses" && !this.responses.loaded) {
        this.fetchSelectionsData();
      }
    },
    refetch() {
      this.improvements.loaded = false;
      this.responses.loaded = false;
      this.escalation.loaded = false;
      this.improvement_area.loaded = false;
      this.noop.loaded = false;

      this.fetchSelectionsData();

      this.fetchFilteredResults();
    },
    changeSquadIds(squadIds) {
      this.squadIds = sanitizeIds(squadIds);
      this.updateQueryParams();
      this.refetch();
    },
    changeTagIds(tagIds) {
      this.tagIds = sanitizeIds(tagIds);
      this.updateQueryParams();
      this.refetch();
    },
    changeBranchId(branchId) {
      this.branchId = branchId;
      this.updateQueryParams();
      this.refetch();
    },
    get params() {
      let paramObj = {
        squad_ids: `${this.squadIds.join(",")}`,
        tag_ids: `${this.tagIds.join(",")}`,
        triage_branch: this.branchId,
      };

      if (this.responses.lastUpdatedFilter && this.tab === "responses") {
        paramObj.last_updated_snapshot_id = this.responses.lastUpdatedFilter;
      }

      return `?${objectToQueryString(paramObj)}`;
    },
    get exportUrl() {
      return `${exportUrl}${this.params}`;
    },
    tooltipIfTruncated(e, fullText) {
      const el = e.target;
      el._tippy?.destroy(); // Delete previous instance

      if (el && el.offsetWidth < el.scrollWidth) {
        tippy(el, {
          content: fullText,
          delay: [250, 0],
          onShow: (instance) => {
            tippy.hideAll({ exclude: instance });
          },
        });
      }
    },

    // Stuff for "Results" tab
    fetchSelectionsData() {
      fetch(`${triageResultsUrl}${this.params}`)
        .then((response) => response.json())
        .then((resp) => {
          this.responses.data = resp.results_by_driver;
          this.responses.numSquads = resp.num_squads;
          this.responses.squadsCompleted = resp.squads_completed;
          this.responses.eligibleSquads = resp.eligible_squads;
          this.responses.escalation_pct = resp.escalation_pct;
          this.responses.improvement_area_pct = resp.improvement_area_pct;
          this.responses.noop_pct = resp.noop_pct;
          this.showImprovementsTab = resp.has_improvements_data;
          this.responses.loaded = true;
          this.initialLoad = true;

          if (resp.results_by_driver.length)
            this.expandDriver(this.sortedSelectionData[0]);
        });
    },
    expandDriver(driver) {
      this.responses.breakdownLoaded = false;
      this.responses.expandedDriver = driver;

      fetch(
        `${teamBreakdownUrl}${this.params}&factor_id=${this.responses.expandedDriver.id}`,
      )
        .then((response) => response.json())
        .then((resp) => {
          this.responses.breakdownData = resp;
          this.responses.breakdownLoaded = true;
        });
    },
    openComments(driver, triageSelection) {
      this.comments.loaded = false;
      this.comments.open = true;
      this.comments.driver = driver;
      this.comments.triageSelection = triageSelection;

      fetch(
        `${snapshotBaseUrl}/comments${this.params}&factor_id=${driver.id}&triage_selection=${triageSelection}`,
      )
        .then((response) => response.json())
        .then((resp) => {
          this.comments.data = resp;
          this.comments.loaded = true;
        });
    },
    get sortedSelectionData() {
      let list = this.responses.data;

      const { sort, sortDir } = this.responses;

      if (sort == "name") {
        list = list.sort((a, b) => a.name.localeCompare(b.name));
      } else if (sort == "votes") {
        list = list.sort((a, b) => {
          const rankA = a.priority_rank || Infinity; // Items without rank go to end
          const rankB = b.priority_rank || Infinity;
          if (rankA == rankB) return a.name.localeCompare(b.name);
          return rankA - rankB;
        });
      } else if (sort == "score") {
        list = list.sort((a, b) => {
          // Push items with hidden/null scores to the end
          if (a.score == null && b.score == null)
            return a.name.localeCompare(b.name);
          if (a.score == null) return 1;
          if (b.score == null) return -1;
          const scoreA = a.score;
          const scoreB = b.score;
          if (scoreA == scoreB) return a.name.localeCompare(b.name);
          return scoreA - scoreB;
        });
      } else {
        // Sort by raw count for triage categories (escalation, improvement_area, noop)
        list = list.sort((a, b) => {
          const countA = a[sort] || 0;
          const countB = b[sort] || 0;

          if (countA == countB) return a.name.localeCompare(b.name);
          return countA - countB;
        });
      }

      if (sortDir === "desc") list = list.reverse();

      return list;
    },
    driverPercentageForSelection(r, opt) {
      const rounded = (
        (100.0 * r[opt]) /
        (r.escalation + r.improvement_area + r.noop)
      ).toFixed(1);

      return parseFloat(rounded);
    },
    optIsExpanded(opt) {
      return this.responses.expandedOpt == opt;
    },
    toggleOptExpanded(opt) {
      if (this.optIsExpanded(opt)) {
        this.responses.expandedOpt = "";
        return;
      }

      this.responses.expandedOpt = opt;
    },
    changeResponsesSort(col) {
      const { sort, sortDir } = this.responses;

      // Close triage menu if open
      this.responses.triageSortMenuOpen = false;

      if (sort == col) {
        this.responses.sortDir = sortDir == "desc" ? "asc" : "desc";
        return;
      }

      this.responses.sort = col;
      this.responses.sortDir = "asc";
    },
    isTriageSort() {
      return ["escalation", "improvement_area", "noop"].includes(
        this.responses.sort,
      );
    },
    toggleTriageSortMenu() {
      this.responses.triageSortMenuOpen = !this.responses.triageSortMenuOpen;
    },
    closeTriageSortMenu() {
      this.responses.triageSortMenuOpen = false;
    },
    selectTriageSort(category) {
      this.responses.sort = category;
      this.responses.sortDir = "desc";
      this.responses.triageSortMenuOpen = false;
    },
    changeLastUpdatedFilter(snapshotId) {
      this.responses.lastUpdatedFilter = snapshotId;
      this.updateQueryParams();
      this.refetch();
    },
    selectSection(section) {
      this.selectedSection = section;
      setQueryStringParam("triage_section", section);
    },
    selectDriver(driver) {
      this.selectedDriver = driver;

      // Reset and fetch all three filtered sections when a driver is selected
      this.escalation.loaded = false;
      this.improvement_area.loaded = false;
      this.noop.loaded = false;

      // Switch to escalation tab when driver is selected (can't show overview)
      if (!this.selectedSection || this.selectedSection === "overview") {
        this.selectSection("escalation");
      }

      // Fetch all three sections with the driver filter
      this.fetchFilteredResults();
    },
    clearSelectedDriver() {
      this.selectedDriver = null;

      // Reset all filtered sections
      this.escalation.loaded = false;
      this.improvement_area.loaded = false;
      this.noop.loaded = false;

      // Switch back to overview when clearing driver selection
      this.selectSection("overview");
    },
    fetchFilteredResults(selection = null) {
      const sections = ["escalation", "improvement_area", "noop"];
      const sectionsToCheck = selection ? [selection] : sections;
      const needsLoad = sectionsToCheck.some(
        (section) => !this[section].loaded,
      );

      if (!needsLoad || this.filteredResultsLoading) return;

      this.filteredResultsLoading = true;

      let url = `${filteredResultsUrl}${this.params}`;
      if (this.selectedDriver) {
        url += `&factor_id=${this.selectedDriver.id}`;
      }

      fetch(url)
        .then((response) => response.json())
        .then((resp) => {
          const results = resp.results || {};
          sections.forEach((section) => {
            this[section].data = results[section] || [];
            this[section].loaded = true;
          });
        })
        .catch((error) => {
          console.error("Error fetching filtered results:", error);
          sections.forEach((section) => {
            if (!this[section].loaded) {
              this[section].data = [];
              this[section].loaded = true;
            }
          });
        })
        .finally(() => {
          this.filteredResultsLoading = false;
        });
    },
    changeFilteredResultsSort(selection, col) {
      const currentSort = this[selection].sortBy;
      const currentReverse = this[selection].sortReverse;

      if (currentSort === col) {
        this[selection].sortReverse = !currentReverse;
      } else {
        this[selection].sortBy = col;
        this[selection].sortReverse = false;
      }
    },
    isFilteredResultsSortOrder(selection, col) {
      return this[selection].sortBy === col;
    },
    getSortedFilteredResults(selection) {
      const data = this[selection].data;
      const sortBy = this[selection].sortBy;
      const sortReverse = this[selection].sortReverse;

      if (!data || data.length === 0) return [];

      let sorted = [...data];

      switch (sortBy) {
        case "team":
          sorted.sort((a, b) => {
            const aKey = (a.team_name || a.actor_name || "").toString();
            const bKey = (b.team_name || b.actor_name || "").toString();
            return aKey.localeCompare(bKey);
          });
          break;
        case "driver":
          sorted.sort((a, b) => {
            const aName = (a.driver_name || "").toString();
            const bName = (b.driver_name || "").toString();
            return aName.localeCompare(bName);
          });
          break;
        case "score":
          sorted.sort((a, b) => {
            // Push items with hidden/null scores to the end
            if (a.score == null && b.score == null) return 0;
            if (a.score == null) return 1;
            if (b.score == null) return -1;
            return a.score - b.score;
          });
          break;
        case "comment":
          // Sort by presence of comment (has text vs. no text)
          sorted.sort((a, b) => {
            const aHasComment = !!(a.comment_text && a.comment_text.trim());
            const bHasComment = !!(b.comment_text && b.comment_text.trim());
            return bHasComment - aHasComment; // Items with comments first
          });
          break;
        default:
          sorted.sort((a, b) => {
            const aName = (a.actor_name || "").toString();
            const bName = (b.actor_name || "").toString();
            return aName.localeCompare(bName);
          });
      }

      return sortReverse ? sorted.reverse() : sorted;
    },

    // Stuff for "Improvements" tab
    fetchImprovementsData() {
      fetch(`${triageImprovementsUrl}${this.params}`)
        .then((response) => response.json())
        .then((resp) => {
          this.improvements.data = resp;
          this.improvements.loaded = true;
        });
    },
    get improvementAreaSuccessRate() {
      const { improvements, num_improvement_areas } = this.improvements.data;
      const denominator = Math.max(num_improvement_areas, 1);

      return Math.round((improvements.length / denominator) * 100);
    },
    get sortedImprovements() {
      let list = this.improvements.data.improvements;

      if (this.improvements.driverFilter) {
        list = list.filter((i) => i.factor === this.improvements.driverFilter);
      }

      if (this.improvements.sort === "squad")
        list = list.sort((a, b) => a.squad.localeCompare(b.squad));
      if (this.improvements.sort === "squad_manager")
        list = list.sort((a, b) =>
          a.manager.name.localeCompare(b.manager.name),
        );
      if (this.improvements.sort === "factor")
        list = list.sort((a, b) => a.factor.localeCompare(b.factor));
      if (this.improvements.sort === "sentiment")
        list = list.sort((a, b) => a.sentiment_change - b.sentiment_change);
      if (this.improvements.sort === "priority")
        list = list.sort((a, b) => -a.priority_change - -b.priority_change);

      if (this.improvements.sortDir === "desc") list = list.reverse();

      return list;
    },
    changeImprovementsSort(col) {
      const { sort, sortDir } = this.improvements;

      if (sort == col) {
        this.improvements.sortDir = sortDir == "desc" ? "asc" : "desc";
        return;
      }

      this.improvements.sort = col;
      this.improvements.sortDir = "desc";
    },
    onChangeDriverFilter(e) {
      this.improvements.driverFilter = e.target.value;
    },
    percentageOfResponses(item, positionCount) {
      if (item.count === 0) return "0%";
      return `${Math.round((100 * item[positionCount]) / item.count)}%`;
    },
  };
}
